/**
 * useSetJobPayoutAmount — thin hook over canonical writer.
 *
 * Role-gated (super_admin / admin / ops_manager). Wraps:
 *   - setJobPayoutAmount() (pure writer)
 *   - setJobs() + appendSystemEvents() (persistence)
 *   - active marketplace/portal offer payout snapshot sync
 *   - sonner toast (user feedback)
 *
 * Returns a stable callback + canSet boolean for conditional UI rendering.
 */

import { useCallback } from "react";
import { toast } from "sonner";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { supabase } from "@/lib/supabase";
import { JobService } from "@/services/job.service";
import {
  setJobPayoutAmount,
  canSetJobPayoutAmount,
  type SetJobPayoutAmountResult,
} from "@/data/contractor/setJobPayoutAmount";
import type { Job } from "@/data/types";

interface UpdateArgs {
  jobId: string;
  amount: number;
  reason?: string;
  materialDivergence?: boolean;
  actor?: string;
  offerCompensationConfig?: Job["offerCompensationConfig"];
}

export function useSetJobPayoutAmount() {
  const { setJobs, marketplaceOffers, setMarketplaceOffers, appendSystemEvents } = useAppData();
  const { role } = useRole();
  const canSet = canSetJobPayoutAmount(role);

  const syncActiveOfferSnapshots = useCallback(
    async (job: Job, amount: number): Promise<unknown | null> => {
      const activeStatuses = new Set(["pending", "accepted", "offered"]);
      const activeOffers = marketplaceOffers.filter(
        (offer) => offer.jobId === job.id && activeStatuses.has(offer.status),
      );

      if (!job.organizationId) {
        return new Error("Missing organization_id for offer compensation sync");
      }

      const targets = activeOffers.length > 0 ? activeOffers : [null];
      for (const offer of targets) {
        const { error: portalError } = await supabase.functions.invoke("opportunity-publish", {
          body: {
            organizationId: job.organizationId,
            appJobId: job.id,
            appJobNumber: job.jobNumber,
            appOfferId: offer?.id,
            contractorId: offer?.contractorId,
            payoutAmountSnapshot: amount,
            refreshOnly: true,
          },
        });
        if (portalError) return portalError;
      }

      setMarketplaceOffers((prev) =>
        prev.map((offer) =>
          offer.jobId === job.id && activeStatuses.has(offer.status)
            ? { ...offer, payoutAmountSnapshot: amount }
            : offer,
        ),
      );

      return null;
    },
    [marketplaceOffers, setMarketplaceOffers],
  );

  const update = useCallback(
    async (args: UpdateArgs): Promise<SetJobPayoutAmountResult> => {
      let result: SetJobPayoutAmountResult = {
        ok: false,
        reason: "forbidden_role",
      } as SetJobPayoutAmountResult;
      let previousJob: Job | null = null;
      setJobs((prev) => {
        previousJob = prev.find((j) => j.id === args.jobId) ?? null;
        result = setJobPayoutAmount(prev, {
          jobId: args.jobId,
          amount: args.amount,
          actor: args.actor ?? role,
          role,
          reason: args.reason,
          materialDivergence: args.materialDivergence,
        });
        return result.ok ? result.jobs : prev;
      });
      if (result.ok) {
        const r = result as Extract<SetJobPayoutAmountResult, { ok: true }>;
        const previousConfig = previousJob?.offerCompensationConfig;
        const nextConfig = args.offerCompensationConfig;
        const configChanged = JSON.stringify(previousConfig ?? null) !== JSON.stringify(nextConfig ?? null);

        if (r.changed || configChanged) {
          const { data, error } = await JobService.update(args.jobId, {
            payoutAmount: r.nextAmount,
            ...(nextConfig !== undefined ? { offerCompensationConfig: nextConfig } : {}),
          });
          if (error || !data) {
            if (previousJob) {
              setJobs((prev) => prev.map((j) => (j.id === previousJob!.id ? previousJob! : j)));
            }
            toast.error("Couldn't update compensation", {
              description: "Saved locally but failed to persist to DB. Changes were reverted.",
            });
            return { ok: false, reason: "not_found" };
          }

          setJobs((prev) => prev.map((j) => (j.id === data.id ? data : j)));

          const offerSyncError = await syncActiveOfferSnapshots(data, r.nextAmount);
          if (offerSyncError) {
            toast.error("Couldn't update offer compensation", {
              description: "The job was saved, but connected offer records could not be updated.",
            });
            return { ok: false, reason: "not_found" };
          }

          if (r.events.length > 0) appendSystemEvents(r.events);
          toast.success("Offer compensation updated", {
            description:
              r.changed
                ? `Set to $${r.nextAmount.toLocaleString()}.`
                : "Compensation settings updated.",
          });
        }
      } else {
        const r = result as Extract<SetJobPayoutAmountResult, { ok: false }>;
        const msg =
          r.reason === "forbidden_role"
            ? "You don't have permission to set offer compensation."
            : r.reason === "invalid_amount"
              ? "Enter a valid compensation amount."
              : r.reason === "reason_required"
                ? "Reason required (≥10 chars) for a material change."
                : "Job not found.";
        toast.error("Couldn't update compensation", { description: msg });
      }
      return result;
    },
    [setJobs, appendSystemEvents, role, syncActiveOfferSnapshots],
  );

  return { update, canSet, role };
}
